import { message, Modal } from "antd";
import { innovitiPaymentCodes } from "../../../../constants/config";
import { v4 as uuidv4 } from "uuid";
import Axios from "axios";
import * as Sentry from "@sentry/react";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import axios from "axios";

import { processRazorpayQRPayment } from "./razorpayQRPaymentService";
import { processCCAvenuePayment } from "./ccavenuePaymentService";
import { upsertWorkflowEventStore } from "../../../../services";
import messages from "../../../../errors/messages.en-IN.json";
import useAuthStore from "../../../../stores/useAuthStore";
import useCartStore from "../../../../stores/useCartStore";
import env from "../../../../config/env";

// Integrated card/EDC payments POST to the hardware controller and block until
// the PAX terminal returns a result. The client HTTP wait must therefore follow
// the terminal's configured transaction timeout, not Axios's global 60000ms
// default (App.js) — otherwise the request aborts mid-transaction and the cashier
// sees "timeout of 60000ms exceeded" while the customer is still paying.
// We read that timeout from the hardware controller status endpoint and add a
// buffer for ACK/framing overhead; when it isn't reported (older controller
// build) we fall back to a fixed wait.
//
// Buffer raised 10000 -> 30000 (2026-08-20) after a field incident: a genuine,
// successful card payment (real auth code, real masked card, terminal-approved)
// took 88.1s end to end — a real customer tapping/inserting a card and entering
// a PIN, confirmed clean in the terminal's own wire trace, nothing wrong on the
// terminal side. The old 10000ms buffer put the abort at 90000ms, just 1.9s
// past that — the AbortController fired 39ms *before* the success response
// reached this function, so the POS showed "canceled" for a payment that had
// already gone through. The cashier retried the same invoice, which then hit a
// stuck connection and started timing out for real. A card can be charged
// successfully and the POS still show nothing for it if this margin is too
// tight — raised well past both the terminal's configured ceiling and
// cw-browser's own internal 85000ms timeout for a single COM operation, so
// this is the last-resort net for cw-browser itself being unresponsive, not
// the thing racing a legitimate slow-but-successful transaction.
const CARD_PAYMENT_TIMEOUT_BUFFER_MS = 30000;
const CARD_PAYMENT_FALLBACK_TIMEOUT_MS = 110000;

// Read the terminal's configured transaction timeout (ms) from the hardware
// controller /status. Returns null when unavailable (older controller, network
// error, or a controller that doesn't report it) so callers use the fallback.
const getTerminalTransactionTimeout = async (hwBaseUrl) => {
  if (!hwBaseUrl) return null;
  try {
    const { data } = await Axios.get(`${hwBaseUrl}status`, { timeout: 5000 });
    const t = Number(data?.transactionTimeout);
    return Number.isFinite(t) && t > 0 ? t : null;
  } catch {
    return null;
  }
};

export const paymentProvider = async (
  amountAdded,
  currentPaymentMethod,
  paymentMethod,
  value,
  cart,
  setCart,
  setLoader,
  setPaymentModal,
  completePayment,
  setSelectedPaymentMethod,
  setAmount,
  setPaytmQrCodeModalOpens,
  setQrCodeResponse,
  setShowCardPaymnteModal,
  setCardPaymnetError,
  setCardPaymnetStatus,
  setCardPaymnetStatusPLB,
  setLoading,
  setIsCardPaymentLoading,
  setLayoutType,
  setKioskUI,
  dispatchQRCodeEvent,
  closeQRCodeModal,
  wallet,
  cardPaymentDetails,
  setIntegratedPaymentStatus,
  showCustomerWallet,
  handleWalletPostPayment,
  isNewGiftCardFlag,
  showGiftCardTopup,
  handleGiftCardTopupPostPayment,
  dispatchCCAvenueEvent,
  closeCCAvenueModal
) => {
  const tillData = useAuthStore.getState().tillData;
  const tillaccess = JSON.parse(tillData?.tillAccess?.userAccessController || "{}");
  const tillLayout = parseInt(tillaccess?.layout == null ? 1 : tillaccess?.layout);
  if (currentPaymentMethod.paymentProvider === "INV" && currentPaymentMethod.integratedPayment === true) {
    processInnovitiEDCPayment(
      amountAdded,
      currentPaymentMethod,
      paymentMethod,
      value,
      tillData,
      tillLayout,
      cart,
      setCart,
      setLoader,
      setPaymentModal,
      completePayment,
      setSelectedPaymentMethod,
      setAmount,
      setLoading,
      wallet
    );
  } else if (currentPaymentMethod.paymentProvider === "PTM" && currentPaymentMethod.integratedPayment === true && !currentPaymentMethod.paymentProviderDetails) {
    processPaytmEDCPayment(
      amountAdded,
      currentPaymentMethod,
      paymentMethod,
      value,
      tillData,
      tillLayout,
      cart,
      setCart,
      setLoader,
      setPaymentModal,
      completePayment,
      setSelectedPaymentMethod,
      setAmount,
      setLoading,
      wallet
    );
  } else if (currentPaymentMethod.paymentProvider === "ADC" && currentPaymentMethod.integratedPayment === true) {
    processADCPayment(
      amountAdded,
      currentPaymentMethod,
      paymentMethod,
      value,
      tillData,
      tillLayout,
      cart,
      setCart,
      setLoader,
      setPaymentModal,
      completePayment,
      setSelectedPaymentMethod,
      setAmount,
      setShowCardPaymnteModal,
      setCardPaymnetError,
      setCardPaymnetStatus,
      setCardPaymnetStatusPLB,
      setLoading,
      setIsCardPaymentLoading,
      setLayoutType,
      setKioskUI,
      wallet,
      cardPaymentDetails,
      setIntegratedPaymentStatus,
      showCustomerWallet,
      handleWalletPostPayment,
      isNewGiftCardFlag,
      showGiftCardTopup,
      handleGiftCardTopupPostPayment
    );
  } else if (
    currentPaymentMethod.integratedPayment === true &&
    ((currentPaymentMethod.paymentProvider === "PLB" && currentPaymentMethod?.paymentProviderDetails?.integrationType === "API") ||
      (currentPaymentMethod.paymentProvider === "RP" && currentPaymentMethod?.paymentProviderDetails?.providerCode === "RPEDC") ||
      (currentPaymentMethod?.paymentProvider === "PTM" && currentPaymentMethod?.paymentProviderDetails?.providerCode === "PYTMEDC"))
  ) {
    processPLBPayment(
      amountAdded,
      currentPaymentMethod,
      paymentMethod,
      value,
      tillData,
      tillLayout,
      cart,
      setCart,
      setLoader,
      setPaymentModal,
      completePayment,
      setSelectedPaymentMethod,
      setAmount,
      setShowCardPaymnteModal,
      setCardPaymnetError,
      setCardPaymnetStatus,
      setCardPaymnetStatusPLB,
      setLoading,
      setIsCardPaymentLoading,
      setLayoutType,
      setKioskUI,
      wallet,
      cardPaymentDetails,
      setIntegratedPaymentStatus,
      showCustomerWallet,
      handleWalletPostPayment,
      isNewGiftCardFlag,
      showGiftCardTopup,
      handleGiftCardTopupPostPayment
    );
  } else if (
    currentPaymentMethod.paymentProvider === "RP" &&
    currentPaymentMethod.integratedPayment === true &&
    currentPaymentMethod?.paymentProviderDetails?.providerCode === "RPGTY"
  ) {
    processRazorpayQRPayment(
      amountAdded,
      currentPaymentMethod,
      paymentMethod,
      value,
      tillData,
      tillLayout,
      cart,
      setCart,
      setLoader,
      setPaymentModal,
      completePayment,
      setSelectedPaymentMethod,
      setAmount,
      setLoading,
      dispatchQRCodeEvent,
      closeQRCodeModal,
      setLayoutType,
      setKioskUI,
      wallet,
      cardPaymentDetails,
      setIntegratedPaymentStatus,
      showCustomerWallet,
      handleWalletPostPayment,
      isNewGiftCardFlag,
      showGiftCardTopup,
      handleGiftCardTopupPostPayment
    );
  } else if (
    currentPaymentMethod.integratedPayment === true &&
    currentPaymentMethod?.paymentProviderDetails?.integrationType === "API" &&
    currentPaymentMethod?.paymentProviderDetails?.providerConfigurations?.some((c) => c.configKey === "MerchantID") &&
    currentPaymentMethod?.paymentProviderDetails?.providerConfigurations?.some((c) => c.configKey === "AccessCode")
  ) {
    processCCAvenuePayment(
      amountAdded,
      currentPaymentMethod,
      paymentMethod,
      value,
      cart,
      setCart,
      setLoader,
      setPaymentModal,
      completePayment,
      setSelectedPaymentMethod,
      setAmount,
      setLoading,
      dispatchCCAvenueEvent,
      closeCCAvenueModal,
      setLayoutType,
      setKioskUI
    );
  } else if (currentPaymentMethod.integratedPayment === false && currentPaymentMethod.paymentProvider === "PTM") {
    processQrPaytmPayment(
      amountAdded,
      currentPaymentMethod,
      paymentMethod,
      value,
      tillData,
      tillLayout,
      cart,
      setCart,
      setLoader,
      setPaymentModal,
      completePayment,
      setSelectedPaymentMethod,
      setAmount,
      setPaytmQrCodeModalOpens,
      setQrCodeResponse,
      setLoading,
      wallet
    );
  } else if (currentPaymentMethod.integratedPayment === true) {
    // BUG-0286/BUG-1962: this catch-all fires whenever integratedPayment is true but
    // paymentProviderDetails didn't match any routing condition above (e.g. a
    // PLB/RP/PTM method missing its expected integrationType/providerCode). The
    // generic toast alone gives support no way to tell which config is wrong, so
    // name the specific missing/mismatched field for diagnosis without changing
    // the existing blocking behavior.
    const reason = describeInvalidProviderConfig(currentPaymentMethod);
    console.error("Invalid payment provider configuration:", {
      reason,
      paymentProvider: currentPaymentMethod.paymentProvider,
      integrationType: currentPaymentMethod?.paymentProviderDetails?.integrationType,
      providerCode: currentPaymentMethod?.paymentProviderDetails?.providerCode,
      paymentProviderDetails: currentPaymentMethod.paymentProviderDetails,
    });
    Sentry.captureMessage("Invalid payment provider configuration", {
      level: "error",
      extra: {
        reason,
        paymentMethodName: currentPaymentMethod.name,
        paymentProvider: currentPaymentMethod.paymentProvider,
        paymentProviderDetails: currentPaymentMethod.paymentProviderDetails,
      },
    });
    message.error(`${messages.errors.payments.invalid_payment_provider}${reason ? ` — ${reason}` : ""}`);
  }
};

// BUG-1962: names the specific config field that failed to match any known
// integrated-payment routing rule, instead of leaving support to guess from a
// generic "Invalid payment provider configuration" toast. Best-effort — covers
// the documented provider codes; falls back to a generic-but-still-concrete
// description for anything else.
const describeInvalidProviderConfig = (method) => {
  const provider = method.paymentProvider;
  const details = method.paymentProviderDetails;
  const integrationType = details?.integrationType;
  const providerCode = details?.providerCode;
  if (provider === "PLB") {
    return `PLB requires paymentProviderDetails.integrationType="API" (got ${integrationType ? `"${integrationType}"` : "missing"})`;
  }
  if (provider === "RP") {
    return `RP requires paymentProviderDetails.providerCode to be "RPEDC" or "RPGTY" (got ${providerCode ? `"${providerCode}"` : "missing"})`;
  }
  if (provider === "PTM") {
    return `PTM (integrated) requires either no paymentProviderDetails, or paymentProviderDetails.providerCode="PYTMEDC" (got ${providerCode ? `"${providerCode}"` : details ? "a config without providerCode" : "no config"})`;
  }
  if (provider === "ADC") {
    return "ADC requires integratedPayment to be the boolean true — check it isn't a truthy string/number";
  }
  if (integrationType === "API") {
    return `provider "${provider || "unknown"}" is API-integrated but missing the CC Avenue providerConfigurations (MerchantID + AccessCode) required for that fallback path`;
  }
  return `paymentProvider "${provider || "unset"}" with paymentProviderDetails ${details ? JSON.stringify(details) : "missing"} does not match any known integrated-payment routing rule`;
};

const processInnovitiEDCPayment = (
  amountAdded,
  currentPaymentMethod,
  paymentMethod,
  value,
  tillData,
  tillLayout,
  cart,
  setCart,
  setLoader,
  setPaymentModal,
  completePayment,
  setSelectedPaymentMethod,
  setAmount,
  setLoading
) => {
  const edcUrl = process.env.REACT_APP_edcUrl;
  if (amountAdded >= 1) {
    const requestCode = innovitiPaymentCodes[currentPaymentMethod.name];
    const trxId = uuidv4().replace(/-/g, "").toUpperCase();
    const cashierId = tillData.tillAccess.csUserId;
    const customerMobileNo = cart.customer.mobileNo;
    setLoader(true);
    setPaymentModal(false);
    console.info(`Innoviti Request URL: ${edcUrl}?value=0,${requestCode},${trxId}!${cashierId}!${customerMobileNo}!,${amountAdded.toString().split(".").join("")}`);
    Axios.get(`${edcUrl}?value=0,${requestCode},${trxId}!${cashierId}!${customerMobileNo}!,${amountAdded.toString().split(".").join("")}`)
      .then((response) => {
        const result = response.data;
        const { ResponseCode, ResponseMessage } = result;
        if (ResponseCode === "00") {
          setLoader(false);
          if (tillLayout === 2) {
            setPaymentModal(true);
          }
          message.success("Payment Success");
          completePayment(paymentMethod, value, cart, setCart, setAmount, setSelectedPaymentMethod, 0);
        } else {
          setLoader(false);
          if (tillLayout === 2) {
            setPaymentModal(true);
          }
          message.error(`${messages.info.payment_failed}: ${ResponseMessage}`);
        }
      })
      .catch((error) => {
        console.error(`${messages.info.payment_failed}:`, error);
        setLoader(false);
        if (tillLayout === 2) {
          setPaymentModal(true);
        }
        message.error(`${messages.info.payment_failed}: ${messages.errors.gift_card.timeout}`);
      });
  } else {
    console.warn("Minimum amount not satisfied");
  }
};

const cancelPaytmVerifyPaymentRequest = (options) => {
  options.data.type = 6;
  Axios(options);
};

const paytmVerifyEDCPaymentStatus = (options) => {
  return new Promise(async function (verify) {
    Modal.confirm({
      title: "Payment request initiated",
      content: "Please verify",
      okText: "Verify",
      okButtonProps: { style: { display: "inline-block" } },
      onOk: () => {
        verify("retry");
      },
      onCancel: () => {
        cancelPaytmVerifyPaymentRequest(options);
        verify("cancel");
      },
    });
  });
};

const chekPaytmEDCPaymentStatus = (options) => {
  return new Promise(async function (process, reject) {
    let retries = true;
    while (retries) {
      try {
        const response = await Axios(options);
        const result = response.data;
        // const result = { "result": 0, "resultCode": "101", "resultMessage": "Transaction already present", "transactionID": null, "authorizationID": null, "request": null, "properties": null };
        if (result.result?.toString() === "200") {
          retries = false;
          process(true);
        } else if (result.result?.toString() === "202" || result.result?.toString() === "203") {
          message.info(result.resultMessage);
          const retryStatus = await paytmVerifyEDCPaymentStatus(options);
          if (retryStatus === "retry") {
            retries = true;
          } else {
            message.error(`${messages.errors.payments.payment_cancelled}`);
            retries = false;
            process(false);
          }
        } else {
          message.error(result.resultMessage);
          retries = false;
          process(false);
        }
      } catch (err) {
        retries = false;
        reject(err);
      }
    }
  });
};

const processPaytmEDCPayment = (
  amountAdded,
  currentPaymentMethod,
  paymentMethod,
  value,
  tillData,
  tillLayout,
  cart,
  setCart,
  setLoader,
  setPaymentModal,
  completePayment,
  setSelectedPaymentMethod,
  setAmount,
  setLoading,
  wallet
) => {
  const trxId = uuidv4().replace(/-/g, "").toUpperCase();
  setLoader(true);
  if (tillLayout === 2) {
    setPaymentModal(true);
  }
  const requestData = {
    url: `${tillData.tillAccess.cwrTill.hardwareController.imageUrl}payment`,
    method: "POST",
    data: {
      type: 0,
      transactionID: trxId,
      terminalID: "111",
      currency: "INR",
      amount: amountAdded,
      properties: {},
      paymentProvider: "Paytm",
    },
    headers: {
      "Content-Type": "Application/json",
    },
  };

  chekPaytmEDCPaymentStatus(requestData)
    .then((response) => {
      if (response) {
        setLoader(false);
        if (tillLayout === 2) {
          setPaymentModal(true);
        }
        message.success("Payment Success");
        completePayment(paymentMethod, value, cart, setCart, setAmount, setSelectedPaymentMethod, 0);
      } else {
        setLoader(false);
        if (tillLayout === 2) {
          setPaymentModal(true);
        }
      }
    })
    .catch((error) => {
      console.error(`${messages.info.payment_failed}:`, error);
      setLoader(false);
      if (tillLayout === 2) {
        setPaymentModal(true);
      }
      message.error(`${messages.info.payment_failed}: ${messages.errors.gift_card.timeout}`);
    });
};

const processQrPaytmPayment = async (
  amountAdded,
  currentPaymentMethod,
  paymentMethod,
  value,
  tillData,
  tillLayout,
  cart,
  setCart,
  setLoader,
  setPaymentModal,
  completePayment,
  setSelectedPaymentMethod,
  setAmount,
  setPaytmQrCodeModalOpens,
  setQrCodeResponse,
  setLoading,
  wallet
) => {
  let setAuthTokens;
  const authHeaders = await getOAuthHeaders();
  if (authHeaders && authHeaders.access_token) {
    setAuthTokens = authHeaders.access_token;
  }
  setPaytmQrCodeModalOpens(true);
  let hostUrl = tillData.tillAccess.cwrTill.hardwareController.imageUrl;
  let paytmUrl = `${hostUrl}paytm/generateQRCode`;
  const getQRCodeValues = {
    midId: "Excelo34085435005810",
    orderId: `${cart.sOrderID}`,
    amount: amountAdded,
    businessType: "UPI_QR_CODE",
    posId: `${tillData.tillAccess.cwrTill.cwrTillID}`,
    merchantKey: "qQUxrwRx@qE6zTxt",
    payTMQRUrl: "https://securegw-stage.paytm.in/paymentservices/qr/create",
    clientId: "C11",
    version: "v1",
  };
  await Axios({
    url: paytmUrl,
    method: "POST",
    data: getQRCodeValues,
    headers: {
      "Content-Type": "application/json",
      Authorization: `${setAuthTokens}`,
    },
  }).then((response) => {
    setQrCodeResponse(response.data);
  });
};

const processADCPayment = async (
  amountAdded,
  currentPaymentMethod,
  paymentMethod,
  value,
  tillData,
  tillLayout,
  cart,
  setCart,
  setLoader,
  setPaymentModal,
  completePayment,
  setSelectedPaymentMethod,
  setAmount,
  setShowCardPaymnteModal,
  setCardPaymnetError,
  setCardPaymnetStatus,
  setCardPaymnetStatusPLB,
  setLoading,
  setIsCardPaymentLoading,
  setLayoutType,
  setKioskUI,
  wallet,
  cardPaymentDetails,
  setIntegratedPaymentStatus,
  showCustomerWallet,
  handleWalletPostPayment,
  isNewGiftCardFlag,
  showGiftCardTopup,
  handleGiftCardTopupPostPayment
) => {
  let setAuthTokens;
  const controller = new AbortController();
  const signal = controller.signal;
  // Abort timer is armed below once we know the terminal's configured timeout.
  let timeoutId = null;
  setCardPaymnetStatus({
    paymentStatus: true,
    message: "Payment is processing, Please wait",
    paymentMode: paymentMethod?.name,
  });
  try {
    // Get OAuth headers
    const authHeaders = await getOAuthHeaders();
    setAuthTokens = authHeaders?.access_token ?? null; // Default to null if access_token is not available

    if (!setAuthTokens) {
      throw new Error("Authorization token is missing");
    }

    // Prepare ADC values
    const hwBaseUrl = tillData?.tillAccess?.cwrTill?.pHWController?.imageUrl ?? "";
    const adcUrl = `${hwBaseUrl}payment`;
    const tillValue = useAuthStore.getState().tillValue || {};
    const nameTill = tillValue?.name ?? "UnknownTill";
    const adcValues = {
      type: 0,
      terminal: nameTill,
      transaction: `${tillData?.tillAccess?.cwrTill?.prefix ?? ""}${cart?.documentno ?? ""}`,
      ReceiptNo: `${tillData?.tillAccess?.cwrTill?.prefix ?? ""}${cart?.documentno ?? ""}`,
      amount: amountAdded - (cart?.paid ?? 0),
      currency: tillData?.tillAccess?.csBunit?.currencies[0]?.isoCode ?? "USD",
      properties: {},
      test: true,
    };

    // Size the HTTP wait to the terminal's configured transaction timeout (admin
    // value, surfaced by the hardware controller /status) plus a buffer; fall back
    // to the existing default when the controller doesn't report it.
    const terminalTimeout = await getTerminalTransactionTimeout(hwBaseUrl);
    const httpTimeout = terminalTimeout ? terminalTimeout + CARD_PAYMENT_TIMEOUT_BUFFER_MS : CARD_PAYMENT_FALLBACK_TIMEOUT_MS;
    // Logged unconditionally so a future "payment succeeded but the POS showed
    // nothing" incident can be diagnosed directly from this timeout value
    // instead of reconstructed after the fact from adjacent log timestamps —
    // that reconstruction is what the 2026-08-20 field incident needed.
    console.log(`[CardPayment] httpTimeout=${httpTimeout}ms (terminalTimeout=${terminalTimeout ?? "unavailable, using fallback"})`);
    timeoutId = setTimeout(() => controller.abort(), httpTimeout);

    // Make POST request to ADC URL
    const response = await Axios.post(adcUrl, adcValues, {
      headers: {
        "Content-Type": "application/xml; charset=UTF-8",
        Accept: "application/xml",
      },
      timeout: httpTimeout,
      signal,
    });
    clearTimeout(timeoutId);
    // Handle ADC response

    if (response?.data?.resultCode === "00" || response?.data?.resultCode === "0000") {
      setShowCardPaymnteModal(false);
      cart.cardPaymentData.payload = [adcValues];
      cart.cardPaymentData.response = [response.data];
      paymentMethod.authorization = response?.data?.authorization ?? "";
      completePayment(paymentMethod, value, cart, setCart, setAmount, setSelectedPaymentMethod, 0);
    } else {
      const errorMessage = response?.data?.resultMessage ?? "Unknown port error";
      setCardPaymnetError(true);
      setCardPaymnetStatus({
        paymentStatus: false,
        message: response.data.resultMessage || "Please check the hardware controller is not responding",
        paymentMode: paymentMethod?.name,
      });

      // Log the error to the server
      const serverUrl = process.env.REACT_APP_serverUrl ?? "";
      const cleanToken = authHeaders?.access_token ?? "";
      const additionalData = {
        tillValue: useAuthStore.getState().tillValue || {},
        adcUrl,
        adcValues,
      };
      const exceptionData = {
        CardPaymentError: {
          response: response?.data ?? {},
          requestData: response?.data?.request ?? {},
          ...additionalData,
        },
      };
      const uniqueId = uuidv4().replace(/-/g, "").toUpperCase();
      try {
        const workFlowResponse = await upsertWorkflowEventStore(uniqueId, "POS", "CE", exceptionData);
      } catch (err) {}
      Sentry.captureException(new Error("Card payment failed"), {
        extra: exceptionData,
      });
    }
  } catch (error) {
    clearTimeout(timeoutId);
    const errorMessage = error?.response?.data?.message || error?.message || "Please check the hardware controller is not responding";
    setCardPaymnetStatus({
      paymentStatus: false,
      message: errorMessage,
      paymentMode: paymentMethod?.name,
    });
    Sentry.captureException(error);
    setCardPaymnetError(true);
    console.error("Error occurred during card payment:", errorMessage);
  }
};

let cancelPolling = false;
export const cancelPLBPolling = () => {
  cancelPolling = true;
};
const processPLBPayment = async (
  amountAdded,
  currentPaymentMethod,
  paymentMethod,
  value,
  tillData,
  tillLayout,
  cart,
  setCart,
  setLoader,
  setPaymentModal,
  completePayment,
  setSelectedPaymentMethod,
  setAmount,
  setShowCardPaymnteModal,
  setCardPaymnetError,
  setCardPaymnetStatus,
  setCardPaymnetStatusPLB,
  setLoading,
  setIsCardPaymentLoading,
  setLayoutType,
  setKioskUI,
  wallet,
  cardPaymentDetails,
  setIntegratedPaymentStatus,
  showCustomerWallet,
  handleWalletPostPayment,
  isNewGiftCardFlag,
  showGiftCardTopup,
  handleGiftCardTopupPostPayment
) => {
  cancelPolling = false;
  const API_URL = env.pineLabsApiUrl;
  const cartObj = cart;
  const userData = useAuthStore.getState().userData;

  if (!cartObj || !userData) {
    console.error("Cart or User data missing.");
    return;
  }
  // setLoading(true);
  setShowCardPaymnteModal(true);
  setIsCardPaymentLoading(true);
  setCardPaymnetError(false);
  setCardPaymnetStatus({
    paymentStatus: true,
    message: "Payment is processing, Please wait",
    paymentMode: paymentMethod?.name,
  });
  try {
    const paymentProviderDetails = paymentMethod.paymentProviderDetails;
    const saleTypeId = paymentMethod.saleTypeId;
    const saleTypeName = paymentMethod.saleTypeName;
    const requestBody = {
      PaymentProviderDetails: paymentProviderDetails,
      SaleTypeId: saleTypeId,
      SaleTypeName: saleTypeName,
      TillId: useAuthStore.getState().tillValue?.name,
      DocumentNo: cart?.documentno,
      meta: [
        {
          key: "mobileNo",
          value: cart?.customer?.mobileNo || "",
        },
      ],
    };

    let typeSegment = "";
    if (wallet && showCustomerWallet) {
      typeSegment = "wallet";
    } else if (isNewGiftCardFlag && showGiftCardTopup) {
      typeSegment = "giftCard";
    }
    // BUG-0286: transactionNumber must be unique per PLB *attempt*, not just per
    // completed payment. The previous suffix was derived from cart.payments/
    // walletPayments — entries that only exist after a payment succeeds — so a
    // retry after a failed/timed-out attempt, or after the browser was refreshed
    // mid-poll, recomputed the exact same suffix and sent an identical
    // transactionNumber to Pine Labs a second time. Track the attempt count in
    // localStorage (written synchronously, survives a refresh) rather than on
    // the cart object — BUG-1963: an earlier version stored this on
    // cart.plbTransactionAttempts via setCart(), but every setCart() call
    // creates a new cart reference, which retriggers useCoreEffects.js's
    // `[cart]`-dependent "auto-fill amount" effect. That effect unconditionally
    // recalculates amount = cart.total - cart.paid (the overall outstanding
    // balance, since this in-flight payment hasn't been applied to cart.paid
    // yet) and overwrote the amount actually being charged for THIS attempt —
    // CardPaymentModal reads that same amount state, so the popup showed the
    // wrong figure for the whole processing wait. localStorage gives the same
    // persistence guarantee without ever touching the cart store.
    const attemptKey = `${cartObj.documentno}:${typeSegment || "std"}-${paymentMethod.finPaymentmethodId}`;
    let attemptsMap = {};
    try {
      attemptsMap = JSON.parse(localStorage.getItem("plbTransactionAttempts") || "{}");
    } catch {
      attemptsMap = {};
    }
    const attemptNumber = (attemptsMap[attemptKey] || 0) + 1;
    attemptsMap[attemptKey] = attemptNumber;
    localStorage.setItem("plbTransactionAttempts", JSON.stringify(attemptsMap));
    const suffix = attemptNumber > 1 ? `-${attemptNumber - 1}` : "";
    const transactionNumber = `${cartObj.documentno}${typeSegment ? `-${typeSegment}` : ""}-${paymentMethod.name}${suffix}`;
    const response = await axios.post(
      `${API_URL}?apiType=uploadTransaction&TransactionNumber=${transactionNumber}&SequenceNumber=1&AllowedPaymentMode=1&Amount=${value}&UserID=${userData.user_id}`,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const { PlutusTransactionReferenceID } = response.data;
    // uploadTransaction's response does not echo the request back under
    // OriginalResponse.OriginalRequest, so that path always resolved to
    // undefined here — reconstruct the complete original request instead
    // (the URL query params the POST used, plus the JSON body) since that's
    // what "original request" means for the payload/checkStatus echo.
    const originalRequest = {
      TransactionNumber: transactionNumber,
      Amount: value,
      UserID: userData.user_id,
      ...requestBody,
    };
    const amount = cartObj.total;

    if (!PlutusTransactionReferenceID) {
      console.error("Transaction Reference ID missing in response.");
      setLoading(false);
      setIsCardPaymentLoading(false);
      return;
    }

    localStorage.setItem("transactionId", PlutusTransactionReferenceID);
    localStorage.setItem("transactionAmount", amount);
    // BUG-1962: originalRequest must be persisted verbatim (not rebuilt later)
    // because it embeds the exact transactionNumber this specific
    // PlutusTransactionReferenceID was uploaded with — recomputing it after the
    // fact would no longer match now that transactionNumber is attempt-counted.
    // This is what lets recheckPLBTransactionStatus() re-query this same
    // transaction after a timeout/refresh instead of only ever starting a new one.
    localStorage.setItem("plbOriginalRequest", JSON.stringify(originalRequest));

    // Step 2: Poll for transaction status
    await pollTransactionStatus(
      PlutusTransactionReferenceID,
      cart,
      paymentMethod,
      value,
      setCart,
      setAmount,
      setSelectedPaymentMethod,
      completePayment,
      setShowCardPaymnteModal,
      setCardPaymnetError,
      setCardPaymnetStatus,
      setCardPaymnetStatusPLB,
      setLoading,
      setIsCardPaymentLoading,
      setLayoutType,
      setKioskUI,
      wallet,
      cardPaymentDetails,
      setIntegratedPaymentStatus,
      showCustomerWallet,
      handleWalletPostPayment,
      isNewGiftCardFlag,
      showGiftCardTopup,
      handleGiftCardTopupPostPayment,
      originalRequest
    );
    // }
  } catch (error) {
    setCardPaymnetError(true);
    setCardPaymnetStatus({
      paymentStatus: false,
      message: "Payment failed. Please try again.",
      paymentMode: paymentMethod?.name,
    });
    setLoading(false);
    setIsCardPaymentLoading(false);
  }
};

const resetTimeoutRef = { current: null };

const pollTransactionStatus = async (
  PlutusTransactionReferenceID,
  cart,
  paymentMethod,
  value,
  setCart,
  setAmount,
  setSelectedPaymentMethod,
  completePayment,
  setShowCardPaymnteModal,
  setCardPaymnetError,
  setCardPaymnetStatus,
  setCardPaymnetStatusPLB,
  setLoading,
  setIsCardPaymentLoading,
  setLayoutType,
  setKioskUI,
  wallet,
  cardPaymentDetails,
  setIntegratedPaymentStatus,
  showCustomerWallet,
  handleWalletPostPayment,
  isNewGiftCardFlag,
  showGiftCardTopup,
  handleGiftCardTopupPostPayment,
  originalRequest,
  attempt = 1,
  maxAttempts = 12,
  interval = 10000
) => {
  if (cancelPolling) return;
  const API_URL = env.pineLabsApiUrl;
  if (attempt > maxAttempts) {
    setLoading(false);
    setIsCardPaymentLoading(false);
    setLayoutType(9);
    localStorage.setItem("layoutType", 9);

    // Dispatch a custom event to notify other parts of the UI
    const layoutEvent = new CustomEvent("customStorageChange", {
      detail: { key: "layoutType", newValue: 9 },
    });
    window.dispatchEvent(layoutEvent);

    setKioskUI(5);
    localStorage.setItem("kioskUI", 5);
    const kioskEvent = new CustomEvent("customStorageChange", {
      detail: { key: "kioskUI", newValue: 5 },
    });
    window.dispatchEvent(kioskEvent);

    // Show the timeout inline in the same Card Payment popup (matching the
    // "Transaction failed"/"Error checking status" branches below) instead of
    // closing this modal and opening a separate Modal.info() dialog — keeps
    // one consistent popup across the whole PLB payment lifecycle.
    setCardPaymnetError(true);
    setCardPaymnetStatus({
      paymentStatus: false,
      title: "Payment Timed Out",
      message: "Your payment request couldn't be processed in time. Please try again.",
      paymentMode: paymentMethod?.name,
    });
    return;
  }
  const paymentProviderDetails = paymentMethod.paymentProviderDetails;
  const saleTypeId = paymentMethod.saleTypeId;
  const saleTypeName = paymentMethod.saleTypeName;
  const details = {
    PaymentProviderDetails: JSON.stringify(paymentProviderDetails),
    SaleTypeId: saleTypeId,
    SaleTypeName: saleTypeName,
    TillId: useAuthStore.getState().tillValue?.name,
    DocumentNo: cart?.documentno,
  };
  let originalRequestStr = JSON.stringify(originalRequest);
  try {
    const queryParams = new URLSearchParams(details).toString(); // Convert object to URL query string
    const statusResponse = await axios.get(`${API_URL}?apiType=checkStatus&transaction_id=${PlutusTransactionReferenceID}&originalRequest=${originalRequestStr}&${queryParams}`, {
      headers: { "Content-Type": "application/json" },
    });
    const { ResponseMessage, TransactionData } = statusResponse.data;
    if (
      ResponseMessage?.trim().toUpperCase() === "TXN APPROVED" ||
      ResponseMessage?.trim().toUpperCase() === "APPROVED" ||
      ResponseMessage?.trim().toUpperCase() === "ACCEPTED_SUCCESS"
    ) {
      setLoading(false);
      setIsCardPaymentLoading(false);
      cancelPolling = true;
      setCardPaymnetError(false);
      setCardPaymnetStatus({
        paymentStatus: true,
        message: "Payment Success",
        paymentMode: paymentMethod?.name,
      });
      const tillData = useAuthStore.getState().tillData;
      const precision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
      // A single card payment method can complete more than one PLB
      // uploadTransaction/checkStatus round trip within the same order (e.g.
      // a split/partial-amount card payment) — accumulate each completed
      // transaction instead of overwriting, so earlier ones aren't lost when
      // completePayment merges the split amounts into one payments[] entry.
      cart.cardPaymentData = cart.cardPaymentData || {};
      cart.cardPaymentData.payload = [...(cart.cardPaymentData.payload || []), originalRequest];
      cart.cardPaymentData.response = [...(cart.cardPaymentData.response || []), statusResponse.data];
      completePayment(
        paymentMethod,
        value,
        cart,
        setCart,
        setAmount,
        setSelectedPaymentMethod,
        0,
        setLayoutType,
        setKioskUI,
        wallet,
        precision,
        useCartStore.getState().setExchangeCart,
        showCustomerWallet,
        handleWalletPostPayment,
        isNewGiftCardFlag,
        showGiftCardTopup,
        handleGiftCardTopupPostPayment
      );
      localStorage.removeItem("transactionId");
      localStorage.removeItem("transactionAmount");
      localStorage.removeItem("plbOriginalRequest");
      setTimeout(() => {
        setShowCardPaymnteModal(false);
      }, 3000);
      return;
    } else if (
      ResponseMessage?.trim().toUpperCase() === "TRANSACTION FAILED" ||
      ResponseMessage?.trim().toUpperCase() === "INVALID PLUTUS TXN REF ID" ||
      ResponseMessage?.trim().toUpperCase() === "UPI Transaction Failed".toLocaleUpperCase()
    ) {
      setLoading(false);
      setIsCardPaymentLoading(false);
      cancelPolling = true;
      setCardPaymnetError(true);
      setCardPaymnetStatus({
        paymentStatus: false,
        message: "Transaction failed or could not be verified.",
        paymentMode: paymentMethod?.name,
      });
      localStorage.removeItem("transactionId");
      localStorage.removeItem("transactionAmount");
      localStorage.removeItem("plbOriginalRequest");

      return;
    }

    // If not approved, retry after delay
    setTimeout(() => {
      pollTransactionStatus(
        PlutusTransactionReferenceID,
        cart,
        paymentMethod,
        value,
        setCart,
        setAmount,
        setSelectedPaymentMethod,
        completePayment,
        setShowCardPaymnteModal,
        setCardPaymnetError,
        setCardPaymnetStatus,
        setCardPaymnetStatusPLB,
        setLoading,
        setIsCardPaymentLoading,
        setLayoutType,
        setKioskUI,
        wallet,
        cardPaymentDetails,
        setIntegratedPaymentStatus,
        showCustomerWallet,
        handleWalletPostPayment,
        isNewGiftCardFlag,
        showGiftCardTopup,
        handleGiftCardTopupPostPayment,
        originalRequest,
        attempt + 1,
        maxAttempts,
        interval
      );
    }, interval);
  } catch (error) {
    console.error("Error checking transaction status:", error);
    setCardPaymnetError(true);
    setCardPaymnetStatus({
      paymentStatus: false,
      message: "Error checking payment status. Retrying...",
      paymentMode: paymentMethod?.name,
    });
    // Retry on failure
    setTimeout(() => {
      pollTransactionStatus(
        PlutusTransactionReferenceID,
        cart,
        paymentMethod,
        value,
        setCart,
        setAmount,
        setSelectedPaymentMethod,
        completePayment,
        setShowCardPaymnteModal,
        setCardPaymnetError,
        setCardPaymnetStatus,
        setCardPaymnetStatusPLB,
        setLoading,
        setIsCardPaymentLoading,
        setLayoutType,
        setKioskUI,
        wallet,
        cardPaymentDetails,
        setIntegratedPaymentStatus,
        showCustomerWallet,
        handleWalletPostPayment,
        isNewGiftCardFlag,
        showGiftCardTopup,
        handleGiftCardTopupPostPayment,
        originalRequest,
        attempt + 1,
        maxAttempts,
        interval
      );
    }, interval);
  }
};

// BUG-1962: mitigation for "bill freezes / doesn't settle" — after
// pollTransactionStatus gives up (12 attempts, 2 minutes), the cashier previously
// had no way to find out whether the customer's swipe actually went through on
// the physical terminal; the only options were start a brand-new charge attempt
// or manually override to Cash. This does a single, on-demand checkStatus call
// against the SAME PlutusTransactionReferenceID (persisted by processPLBPayment)
// so staff can confirm/deny before deciding. This does not fix why Pine Labs
// didn't resolve within the poll window — that needs live gateway logs — it only
// gives a recovery path for the already-submitted transaction.
export const recheckPLBTransactionStatus = async (
  cart,
  setCart,
  paymentMethod,
  setAmount,
  setSelectedPaymentMethod,
  completePayment,
  setShowCardPaymnteModal,
  setCardPaymnetError,
  setCardPaymnetStatus,
  showCustomerWallet,
  handleWalletPostPayment,
  isNewGiftCardFlag,
  showGiftCardTopup,
  handleGiftCardTopupPostPayment,
  wallet
) => {
  const API_URL = env.pineLabsApiUrl;
  const transactionId = localStorage.getItem("transactionId");
  let originalRequest = null;
  try {
    originalRequest = JSON.parse(localStorage.getItem("plbOriginalRequest") || "null");
  } catch {
    originalRequest = null;
  }
  if (!transactionId || !originalRequest) {
    setCardPaymnetStatus({
      paymentStatus: false,
      title: "Payment Timed Out",
      message: "No pending Pine Labs transaction found to check. Please start a new payment attempt.",
      paymentMode: paymentMethod?.name,
    });
    return;
  }
  setCardPaymnetStatus({
    paymentStatus: false,
    title: "Checking Status",
    message: "Checking the payment status with Pine Labs, please wait...",
    paymentMode: paymentMethod?.name,
  });
  const details = {
    PaymentProviderDetails: JSON.stringify(originalRequest.PaymentProviderDetails),
    SaleTypeId: originalRequest.SaleTypeId,
    SaleTypeName: originalRequest.SaleTypeName,
    TillId: originalRequest.TillId,
    DocumentNo: originalRequest.DocumentNo,
  };
  try {
    const queryParams = new URLSearchParams(details).toString();
    const originalRequestStr = JSON.stringify(originalRequest);
    const statusResponse = await axios.get(
      `${API_URL}?apiType=checkStatus&transaction_id=${transactionId}&originalRequest=${originalRequestStr}&${queryParams}`,
      { headers: { "Content-Type": "application/json" } }
    );
    const normalized = statusResponse.data?.ResponseMessage?.trim().toUpperCase();
    if (normalized === "TXN APPROVED" || normalized === "APPROVED" || normalized === "ACCEPTED_SUCCESS") {
      setCardPaymnetError(false);
      setCardPaymnetStatus({
        paymentStatus: true,
        message: "Payment Success",
        paymentMode: paymentMethod?.name,
      });
      const tillData = useAuthStore.getState().tillData;
      const precision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
      cart.cardPaymentData = cart.cardPaymentData || {};
      cart.cardPaymentData.payload = [...(cart.cardPaymentData.payload || []), originalRequest];
      cart.cardPaymentData.response = [...(cart.cardPaymentData.response || []), statusResponse.data];
      // F-02: cleanup moved into finally so it runs even if completePayment() throws
      // (defense in depth alongside the optional-chaining fix in paymentController.js).
      // Without this, a throw here left transactionId/plbOriginalRequest in localStorage,
      // so the next "check again" click re-approved and re-tendered the same money.
      try {
        completePayment(
          paymentMethod,
          originalRequest.Amount,
          cart,
          setCart,
          setAmount,
          setSelectedPaymentMethod,
          0,
          null,
          null,
          wallet,
          precision,
          null,
          showCustomerWallet,
          handleWalletPostPayment,
          isNewGiftCardFlag,
          showGiftCardTopup,
          handleGiftCardTopupPostPayment
        );
      } finally {
        localStorage.removeItem("transactionId");
        localStorage.removeItem("transactionAmount");
        localStorage.removeItem("plbOriginalRequest");
      }
      // F-16: close the modal on a successful recheck, matching pollTransactionStatus's
      // sibling success branch — otherwise it stays open over a completed transaction,
      // inviting the cashier to tender again. Only reached if completePayment() didn't
      // throw (a throw propagates past this point to the outer catch below).
      setTimeout(() => {
        setShowCardPaymnteModal(false);
      }, 3000);
    } else if (normalized === "TRANSACTION FAILED" || normalized === "INVALID PLUTUS TXN REF ID" || normalized === "UPI TRANSACTION FAILED") {
      setCardPaymnetError(true);
      setCardPaymnetStatus({
        paymentStatus: false,
        message: "Transaction failed or could not be verified.",
        paymentMode: paymentMethod?.name,
      });
      localStorage.removeItem("transactionId");
      localStorage.removeItem("transactionAmount");
      localStorage.removeItem("plbOriginalRequest");
    } else {
      setCardPaymnetError(true);
      setCardPaymnetStatus({
        paymentStatus: false,
        title: "Payment Timed Out",
        message: "Still no confirmation from Pine Labs. You can check again, or start a new payment attempt.",
        paymentMode: paymentMethod?.name,
      });
    }
  } catch (error) {
    console.error("Error rechecking PLB transaction status:", error);
    setCardPaymnetError(true);
    setCardPaymnetStatus({
      paymentStatus: false,
      title: "Payment Timed Out",
      message: "Error checking payment status. You can check again, or start a new payment attempt.",
      paymentMode: paymentMethod?.name,
    });
  }
};
